import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { Package, CalendarDays, FileText, Users2, Vote, Plus, Check, X, Receipt, PieChart as PieIcon, AlertTriangle, Award, Upload } from "lucide-react";
import ReservationsCenter from "./ReservationsCenter";
import { PublicationsMgr, BoletosMgr, MultasMgr } from "./ServiceForms";

const inputCls = "w-full rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/50 transition-colors";

function Packages() {
  const qc = useQueryClient();
  const { data: pkgs, isLoading } = useQuery({ queryKey: ["mgr-packages"], queryFn: async () => (await api.get("/packages")).data });
  const { data: residents } = useQuery({ queryKey: ["mgr-approved"], queryFn: async () => (await api.get("/residents", { params: { status: "approved" } })).data });
  const [resId, setResId] = useState(""); const [desc, setDesc] = useState("Encomenda");

  const create = async () => {
    if (!resId) return toast.error("Selecione o morador.");
    try { await api.post("/packages", { resident_id: resId, description: desc }); qc.invalidateQueries({ queryKey: ["mgr-packages"] }); setResId(""); toast.success("Encomenda registrada! Morador notificado."); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Registrar encomenda</p>
        <select value={resId} onChange={(e) => setResId(e.target.value)} data-testid="pkg-resident" className={inputCls}>
          <option value="" className="bg-background">Selecione o morador</option>
          {residents?.map((r) => <option key={r.id} value={r.id} className="bg-background">{r.name} — {r.tower}/{r.unit}</option>)}
        </select>
        <input value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="pkg-desc" placeholder="Descrição" className={inputCls} />
        <button onClick={create} data-testid="pkg-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Registrar e avisar morador</button>
      </div>
      {isLoading ? <Loading /> : pkgs?.map((p) => (
        <div key={p.id} data-testid="mgr-package-item" className="glass rounded-2xl p-4 flex items-center gap-3">
          <span className="text-xl">📦</span>
          <div className="flex-1"><p className="text-sm font-semibold">{p.resident?.name} — {p.resident?.tower}/{p.resident?.unit}</p><p className="text-xs text-muted-foreground">{p.description}</p></div>
          {p.status === "aguardando" ? (
            <button onClick={async () => { try { await api.post(`/packages/${p.id}/pickup`); qc.invalidateQueries({ queryKey: ["mgr-packages"] }); toast.success("Retirada confirmada."); } catch (e) { toast.error(apiError(e)); } }} data-testid="mgr-confirm-pickup" className="text-xs px-3 py-1.5 rounded-full brand-gradient text-white font-semibold">Confirmar retirada</button>
          ) : (
            <span className="text-xs text-emerald-400">retirada</span>
          )}
        </div>
      ))}
    </div>
  );
}

function Reservations() {
  return <ReservationsCenter />;
}

function Documents() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-documents"], queryFn: async () => (await api.get("/documents")).data });
  const [f, setF] = useState({ title: "", category: "Regimento interno", content: "" });
  const create = async () => {
    if (!f.title.trim()) return toast.error("Informe o título.");
    try { await api.post("/documents", f); qc.invalidateQueries({ queryKey: ["mgr-documents"] }); setF({ title: "", category: "Regimento interno", content: "" }); toast.success("Documento adicionado! Ele alimenta o Pergunte ao KONDDO."); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Adicionar documento</p>
        <p className="text-xs text-muted-foreground">O conteúdo abaixo alimenta o assistente Pergunte ao KONDDO.</p>
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="doc-category" className={inputCls}>
          {["Convenção", "Regimento interno", "Atas", "Comunicados", "Manuais", "Outros"].map((c) => <option key={c} className="bg-background">{c}</option>)}
        </select>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="doc-title" placeholder="Título" className={inputCls} />
        <textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="doc-content" rows={3} placeholder="Conteúdo / regras (usado pela IA)" className={`${inputCls} resize-none`} />
        <button onClick={create} data-testid="doc-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Adicionar</button>
      </div>
      {isLoading ? <Loading /> : data?.map((d) => (
        <div key={d.id} className="glass rounded-2xl p-4 flex items-center gap-3"><FileText className="w-5 h-5 text-primary" /><div className="flex-1"><p className="text-sm font-semibold">{d.title}</p><p className="text-xs text-muted-foreground">{d.category}</p></div></div>
      ))}
    </div>
  );
}

function Assemblies() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-assemblies"], queryFn: async () => (await api.get("/assemblies")).data });
  const [f, setF] = useState({ title: "", agenda: "", date: "", time: "", status: "futura" });
  const create = async () => {
    if (!f.title.trim() || !f.date) return toast.error("Preencha título e data.");
    try { await api.post("/assemblies", f); qc.invalidateQueries({ queryKey: ["mgr-assemblies"] }); setF({ title: "", agenda: "", date: "", time: "", status: "futura" }); toast.success("Assembleia criada!"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Nova assembleia</p>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="asm-title" placeholder="Título" className={inputCls} />
        <textarea value={f.agenda} onChange={(e) => setF({ ...f, agenda: e.target.value })} data-testid="asm-agenda" rows={2} placeholder="Pauta" className={`${inputCls} resize-none`} />
        <div className="grid grid-cols-2 gap-3">
          <input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="asm-date" className={`${inputCls} [color-scheme:dark]`} />
          <input value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="asm-time" placeholder="19:30" className={inputCls} />
        </div>
        <button onClick={create} data-testid="asm-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Criar assembleia</button>
      </div>
      {isLoading ? <Loading /> : data?.map((a) => (
        <div key={a.id} data-testid="mgr-assembly" className="glass rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">{a.title}</p>
            <span className="text-xs text-muted-foreground">👥 {a.attendance_count || 0}</span>
          </div>
          <p className="text-xs text-muted-foreground">{new Date(a.date).toLocaleDateString("pt-BR")} · {a.time} · {a.status}</p>
          <label className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> {a.ata_url ? "Substituir ata" : "Anexar ata"}
            <input type="file" className="hidden" data-testid="ata-upload" onChange={async (e) => {
              const file = e.target.files[0]; if (!file) return;
              try {
                const fd = new FormData(); fd.append("file", file);
                const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                await api.put(`/assemblies/${a.id}/ata`, { ata_url: up.path });
                qc.invalidateQueries({ queryKey: ["mgr-assemblies"] }); toast.success("Ata anexada!");
              } catch (err) { toast.error(apiError(err)); }
            }} />
          </label>
          {a.ata_url && <a href={fileUrl(a.ata_url)} target="_blank" rel="noreferrer" className="ml-3 text-xs text-muted-foreground underline">ver ata</a>}
        </div>
      ))}
    </div>
  );
}

function Polls() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["mgr-polls"], queryFn: async () => (await api.get("/polls")).data });
  const [question, setQuestion] = useState(""); const [options, setOptions] = useState(["", ""]);
  const create = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || opts.length < 2) return toast.error("Informe a pergunta e ao menos 2 opções.");
    try { await api.post("/polls", { question, options: opts }); qc.invalidateQueries({ queryKey: ["mgr-polls"] }); setQuestion(""); setOptions(["", ""]); toast.success("Enquete criada!"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="glass rounded-3xl p-5 space-y-3">
        <p className="font-display font-semibold">Nova enquete</p>
        <input value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="poll-question" placeholder="Pergunta" className={inputCls} />
        {options.map((o, i) => (
          <input key={i} value={o} onChange={(e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} data-testid={`poll-opt-${i}`} placeholder={`Opção ${i + 1}`} className={inputCls} />
        ))}
        <button onClick={() => setOptions([...options, ""])} className="text-xs text-primary flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Adicionar opção</button>
        <button onClick={create} data-testid="poll-submit" className="w-full rounded-full brand-gradient py-2.5 text-white font-semibold text-sm active:scale-95 transition-transform">Criar enquete</button>
      </div>
      {isLoading ? <Loading /> : data?.map((p) => (
        <div key={p.id} data-testid="mgr-poll" className="glass rounded-3xl p-4">
          <p className="font-display font-semibold text-sm">🗳️ {p.question}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{p.active ? "Ativa" : "Encerrada"} · {p.total_votes} participante(s)</p>
          <div className="mt-3 space-y-1.5">
            {p.options.map((opt) => {
              const c = p.counts?.[opt] || 0;
              const pct = p.total_votes ? Math.round((c / p.total_votes) * 100) : 0;
              return (
                <div key={opt} className="relative rounded-lg overflow-hidden border border-white/10">
                  <div className="absolute inset-y-0 left-0 brand-gradient opacity-25" style={{ width: `${pct}%` }} />
                  <div className="relative flex justify-between px-3 py-1.5 text-xs"><span>{opt}</span><span className="text-muted-foreground">{c} voto(s) · {pct}%</span></div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { key: "encomendas", label: "Encomendas", icon: Package },
  { key: "reservas", label: "Reservas", icon: CalendarDays },
  { key: "enquetes", label: "Enquetes", icon: Vote },
  { key: "assembleias", label: "Assembleias", icon: Users2 },
  { key: "boletos", label: "Boletos", icon: Receipt },
  { key: "multas", label: "Multas", icon: AlertTriangle },
  { key: "certidoes", label: "Certidões", icon: Award },
  { key: "regimento", label: "Regimento", icon: FileText },
  { key: "manuais", label: "Manuais", icon: FileText },
];
const VIEWS = {
  encomendas: Packages, reservas: Reservations, documentos: Documents, assembleias: Assemblies, enquetes: Polls,
  boletos: BoletosMgr, multas: MultasMgr,
  certidoes: () => <PublicationsMgr type="certidao" label="Certidão" />,
  regimento: () => <PublicationsMgr type="regimento" label="Regimento Interno" allowIndividual={false} />,
  manuais: () => <PublicationsMgr type="manual" label="Manual" allowIndividual={false} />,
};

export default function ServicesMgmt() {
  const [tab, setTab] = useState("encomendas");
  const View = VIEWS[tab];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Gestão dos serviços</h1>
        <p className="text-sm text-muted-foreground">Administre encomendas, reservas, documentos e mais.</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`svc-tab-${t.key}`} className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.key ? "brand-gradient text-white" : "bg-white/5 border border-white/10 text-muted-foreground"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      <View />
    </div>
  );
}
